import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History, Loader2, Undo2 } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';

import type { MesclagemResponse } from '@/api/generated/model';
import { getExamesControllerListarQueryKey } from '@/api/generated/exames/exames';
import { getPacientesControllerListarQueryKey } from '@/api/generated/pacientes/pacientes';
import {
  getMesclagemDePacientesControllerListarQueryKey,
  useMesclagemDePacientesControllerDesfazer,
  useMesclagemDePacientesControllerListar,
} from '@/api/generated/pacientes-mesclagens/pacientes-mesclagens';
import { ApiError } from '@/api/http-client';
import { resumirTotais } from '@/features/pacientes/lib/mesclagem';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

const ORIGEM: Record<string, string> = {
  MANTIDO: 'do que ficou',
  ABSORVIDO: 'do juntado',
  AMBOS: 'os dois juntos',
  AUTOMATICO: 'automático',
};

function dataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Histórico de junções de pacientes do ambiente, com o "desfazer".
 *
 * Mostra quem juntou, quando, de onde veio cada informação diferente e quantos
 * registros mudaram de paciente. Desfazer devolve tudo ao paciente juntado e o
 * reativa; o que foi editado no paciente que ficou DEPOIS da junção é mantido.
 */
export function HistoricoJuncoesDialogo({ aberto, aoFechar }: Props) {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [aberta, setAberta] = useState<string | null>(null);
  const [aDesfazer, setADesfazer] = useState<MesclagemResponse | null>(null);

  useEffect(() => {
    if (aberto) return;
    setPagina(1);
    setBusca('');
    setAberta(null);
  }, [aberto]);

  const { data, isPending, isError, refetch } = useMesclagemDePacientesControllerListar(
    {
      pagina,
      porPagina: 10,
      ordenarPor: 'criadoEm',
      ordem: 'desc',
      ...(busca.trim() ? { busca: busca.trim() } : {}),
    },
    { query: { enabled: aberto } },
  );

  const desfazer = useMesclagemDePacientesControllerDesfazer();

  async function confirmarDesfazer() {
    if (!aDesfazer) return;
    try {
      await desfazer.mutateAsync({ id: aDesfazer.id });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getMesclagemDePacientesControllerListarQueryKey(),
        }),
        queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() }),
      ]);
      toast.success(
        `Junção desfeita: "${aDesfazer.absorvido.nome}" voltou a ser um paciente separado.`,
      );
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível desfazer a junção.');
    } finally {
      setADesfazer(null);
    }
  }

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-brand" />
              Histórico de junções de pacientes
            </DialogTitle>
            <DialogDescription>
              Cada junção registra quem juntou, quando e de qual paciente veio cada informação.
            </DialogDescription>
          </DialogHeader>

          <Input
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPagina(1);
            }}
            placeholder="Buscar pelo nome de um dos pacientes..."
            aria-label="Buscar junções"
          />

          {isPending ? (
            <EstadoCarregando />
          ) : isError || !data ? (
            <EstadoErro aoTentarDeNovo={() => void refetch()} />
          ) : data.itens.length === 0 ? (
            <EstadoVazio
              icone={History}
              titulo="Nenhuma junção"
              descricao={
                busca
                  ? 'Nenhuma junção com esse paciente.'
                  : 'Quando dois pacientes duplicados forem juntados, a junção aparece aqui.'
              }
            />
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="w-8" />
                      <th className="px-3 py-2 font-medium">Data</th>
                      <th className="px-3 py-2 font-medium">Junção</th>
                      <th className="px-3 py-2 font-medium">Por</th>
                      <th className="px-3 py-2 font-medium">Situação</th>
                      <th className="px-3 py-2 font-medium w-28">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.itens.map((m) => {
                      const expandida = aberta === m.id;
                      return (
                        <Fragment key={m.id}>
                          <tr className="border-t border-border">
                            <td className="pl-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                aria-label={expandida ? 'Recolher detalhes' : 'Ver detalhes'}
                                aria-expanded={expandida}
                                onClick={() => setAberta(expandida ? null : m.id)}
                              >
                                {expandida ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </Button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                              {dataHora(m.criadoEm)}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-medium">{m.absorvido.nome}</span>
                              <span className="text-muted-foreground"> juntado em </span>
                              <span className="font-medium">{m.mantido.nome}</span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {m.autorNome ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              {m.desfeitaEm ? (
                                <Badge variant="outline" className="font-normal text-amber-700">
                                  Desfeita em {dataHora(m.desfeitaEm)}
                                  {m.desfeitaPorNome ? ` por ${m.desfeitaPorNome}` : ''}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="font-normal text-brand">
                                  Ativa
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {!m.desfeitaEm && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!m.podeDesfazer}
                                  title={m.motivoNaoPodeDesfazer ?? undefined}
                                  onClick={() => setADesfazer(m)}
                                >
                                  <Undo2 className="w-4 h-4" />
                                  Desfazer
                                </Button>
                              )}
                            </td>
                          </tr>
                          {expandida && (
                            <tr className="bg-muted/20">
                              <td />
                              <td colSpan={5} className="px-3 py-3">
                                <DetalheDaJuncao mesclagem={m} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Paginacao
                pagina={data.pagina}
                totalPaginas={data.totalPaginas}
                total={data.total}
                porPagina={data.porPagina}
                aoMudarPagina={setPagina}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(aDesfazer)} onOpenChange={(v) => !v && setADesfazer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer a junção?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{aDesfazer?.absorvido.nome}&quot; volta a ser um paciente separado, com os
              registros que eram dele. As informações do cadastro de &quot;
              {aDesfazer?.mantido.nome}&quot; voltam a ser as de antes da junção, exceto as que
              foram editadas depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={desfazer.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmarDesfazer();
              }}
              disabled={desfazer.isPending}
            >
              {desfazer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Desfazer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DetalheDaJuncao({ mesclagem }: { mesclagem: MesclagemResponse }) {
  // Os campos iguais não contam história nenhuma — só o que mudou ou foi escolhido.
  const relevantes = mesclagem.campos.filter((c) => c.status !== 'IGUAL');

  return (
    <div className="space-y-3 text-sm">
      <p>
        <span className="text-muted-foreground">
          Registros que passaram para {mesclagem.mantido.nome}:{' '}
        </span>
        {resumirTotais(mesclagem.totais)}.
      </p>

      {relevantes.length === 0 ? (
        <p className="text-muted-foreground">Os dois cadastros eram iguais.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Campo</th>
              <th className="py-1 pr-3 font-medium">{mesclagem.mantido.nome}</th>
              <th className="py-1 pr-3 font-medium">{mesclagem.absorvido.nome}</th>
              <th className="py-1 pr-3 font-medium">Ficou</th>
            </tr>
          </thead>
          <tbody>
            {relevantes.map((c) => (
              <tr key={c.campo} className="border-t border-border/60 align-top">
                <td className="py-1 pr-3 font-medium">{c.rotulo}</td>
                <td className="py-1 pr-3 whitespace-pre-line">{c.textoMantido ?? '—'}</td>
                <td className="py-1 pr-3 whitespace-pre-line">{c.textoAbsorvido ?? '—'}</td>
                <td className="py-1 pr-3">
                  <span className="whitespace-pre-line">{c.textoFinal ?? '—'}</span>{' '}
                  <Badge variant="outline" className="text-xs font-normal">
                    {ORIGEM[c.origem] ?? c.origem}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
